import { useState, type CSSProperties } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { LongChatPosition } from '../types/widget';

const TooltipArrow = () => (
    <svg
        width="18"
        height="10"
        viewBox="0 0 18 10"
        xmlns="http://www.w3.org/2000/svg"
        style={{
            display: 'block',
            filter: 'drop-shadow(0 2px 4px rgba(15, 23, 42, 0.08))',
        }}
    >
        <path
            d="M2 0H16L10.2 7.2C9.6 8 8.4 8 7.8 7.2L2 0Z"
            fill="white"
        />
    </svg>
);

const ChatIcon = ({ size }: { size: number }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: `${size}px`, height: `${size}px` }}
    >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

const CloseIcon = ({ size }: { size: number }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: `${size}px`, height: `${size}px` }}
    >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

interface FloatingButtonProps {
    onClick: () => void;
    isOpen: boolean;
    position: LongChatPosition;
    tooltip?: string;
    primaryColor?: string;
    buttonSize?: number;
    offsetX?: number;
    offsetY?: number;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

export function FloatingButton({
                                   onClick,
                                   isOpen,
                                   position,
                                   tooltip,
                                   primaryColor = '#FF3E6C',
                                   buttonSize = 56,
                                   offsetX = 24,
                                   offsetY = 24,
                               }: FloatingButtonProps) {
    const [isTooltipDismissed, setIsTooltipDismissed] = useState(false);

    const safeButtonSize = clamp(Math.round(buttonSize), 40, 96);
    const safeOffsetX = clamp(Math.round(offsetX), 0, 200);
    const safeOffsetY = clamp(Math.round(offsetY), 0, 200);
    const iconSize = clamp(Math.round(safeButtonSize * 0.5), 20, 40);

    const isTop = position.startsWith('top-');
    const isLeft = position.endsWith('-left');

    const horizontalOffset = `min(${safeOffsetX}px, calc(100vw - ${safeButtonSize + 8}px))`;
    const verticalOffset = `min(${safeOffsetY}px, calc(100vh - ${safeButtonSize + 8}px))`;

    const wrapperStyle: CSSProperties = {
        top: isTop ? verticalOffset : 'auto',
        bottom: isTop ? 'auto' : verticalOffset,
        left: isLeft ? horizontalOffset : 'auto',
        right: isLeft ? 'auto' : horizontalOffset,
    };

    const tooltipStyle: CSSProperties = {
        top: isTop ? `${safeButtonSize + 12}px` : 'auto',
        bottom: isTop ? 'auto' : `${safeButtonSize + 12}px`,
        left: isLeft ? '0' : 'auto',
        right: isLeft ? 'auto' : '0',
        transformOrigin: `${isLeft ? 'left' : 'right'} ${isTop ? 'top' : 'bottom'}`,
    };

    const arrowStyle: CSSProperties = {
        top: isTop ? '-8px' : 'auto',
        bottom: isTop ? 'auto' : '-8px',
        left: isLeft ? '14px' : 'auto',
        right: isLeft ? 'auto' : '14px',
        transform: isTop ? 'rotate(180deg)' : undefined,
    };

    const showTooltip = Boolean(tooltip && !isOpen && !isTooltipDismissed);

    return (
        <div className="lc-fixed lc-z-[9999]" style={wrapperStyle}>
            <AnimatePresence>
                {showTooltip && (
                    <motion.div
                        initial={{ opacity: 0, y: isTop ? -8 : 8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: isTop ? -8 : 8, scale: 0.96 }}
                        transition={{ duration: 0.18 }}
                        className="lc-absolute lc-pointer-events-auto"
                        style={tooltipStyle}
                    >
                        <div className="lc-relative lc-inline-flex lc-items-center lc-gap-1.5 lc-bg-white lc-px-2.5 lc-py-1.5 lc-rounded-full lc-shadow-lg lc-whitespace-nowrap">
                            <button
                                type="button"
                                aria-label="סגור טולטיפ"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsTooltipDismissed(true);
                                }}
                                className="lc-flex lc-items-center lc-justify-center lc-w-4 lc-h-4 lc-rounded-full lc-border-none lc-bg-transparent lc-cursor-pointer lc-text-gray-400 hover:lc-bg-gray-100"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lc-w-2.5 lc-h-2.5"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>

                            <span
                                className="lc-text-[11px] lc-font-semibold"
                                style={{
                                    color: primaryColor,
                                    fontFamily: 'Heebo, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                    letterSpacing: '0.1px',
                                }}
                            >
                                {tooltip}
                            </span>

                            <div className="lc-absolute" style={arrowStyle}>
                                <TooltipArrow />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                type="button"
                onClick={onClick}
                aria-label={tooltip}
                className="lc-border-none lc-cursor-pointer lc-flex lc-items-center lc-justify-center lc-text-white lc-rounded-full"
                style={{
                    backgroundColor: primaryColor,
                    width: `${safeButtonSize}px`,
                    height: `${safeButtonSize}px`,
                    minWidth: `${safeButtonSize}px`,
                    minHeight: `${safeButtonSize}px`,
                    maxWidth: `${safeButtonSize}px`,
                    maxHeight: `${safeButtonSize}px`,
                    borderRadius: '9999px',
                    aspectRatio: '1 / 1',
                    padding: '0',
                    boxShadow: `0 10px 26px ${primaryColor}4d, 0 4px 12px rgba(15, 23, 42, 0.16)`,
                }}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.94 }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            >
                <AnimatePresence mode="wait">
                    {isOpen ? (
                        <motion.div
                            key="close"
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <CloseIcon size={iconSize} />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="chat"
                            initial={{ rotate: 90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ChatIcon size={iconSize} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.button>
        </div>
    );
}
